import { supabase } from '../../../lib/supabase'
import { daysLeftInWeek as canonicalDaysLeftInWeek } from '../../../lib/api/weekly'

/** A row in `three_step_weekly_events`. */
export interface ThreeStepWeeklyEventRow {
  id: string
  week_start: string
  week_end: string
  /** First action phrase — slots into "How do you {a}…". */
  action_a: string
  /** Second action phrase — slots into "…while {b} at the same time". */
  action_b: string
  is_final: boolean
  created_at: string
}

/** A row in `three_step_weekly_entries`. */
export interface ThreeStepWeeklyEntryRow {
  id: string
  event_id: string
  profile_id: string
  step1_text: string
  step2_text: string
  step3_text: string
  total_votes: number
  is_final: boolean
  created_at: string
  updated_at: string
}

/** Shape returned by `list_three_step_weekly_winners`. */
export interface ThreeStepWeeklyWinnerRow {
  event_id: string
  week_start: string
  week_end: string
  action_a: string
  action_b: string
  author_id: string
  author_name: string
  total_votes: number
}

/**
 * The famous prompt template. Always wraps the two actions verbatim;
 * the action pool entries are stored as bare verb phrases (e.g.
 * "walking the dog") and the template stays here so we can tweak the
 * wording without re-seeding the pool.
 */
export function buildPromptText(
  action_a: string, action_b: string,
  t: (k: string, vars?: Record<string, string | number>) => string = (k, v) =>
    Object.entries(v ?? {}).reduce((acc, [key, val]) => acc.split(`{${key}}`).join(String(val)), k),
): string {
  // One key, both actions as placeholders. Never split this into
  // fragments around the actions: word order moves between languages
  // (Japanese puts the verb last) and fragments cannot express that.
  // The two action phrases are player-written and stay in their own
  // language; only the wrapper translates.
  return t('How do you {a} while {b} at the same time, in three steps?', { a: action_a, b: action_b })
}

/**
 * Get-or-create this week's event. Same pattern as IET / Colorful
 * weekly — pulling this on first request also finalizes any prior
 * un-finalized week so it shifts into the vote / archive surface.
 */
export async function getOrCreateThreeStepWeekly(
  weekStart: string,
): Promise<ThreeStepWeeklyEventRow> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('get_or_create_three_step_weekly_event', {
    p_week_start: weekStart,
  })
  if (error) throw error
  return data as ThreeStepWeeklyEventRow
}

/** The most recent finalized event (for the "Last week" vote card). */
export async function fetchLastFinalizedThreeStepWeekly(
  beforeWeekStart: string,
): Promise<ThreeStepWeeklyEventRow | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('three_step_weekly_events')
    .select('*')
    .eq('is_final', true)
    .lt('week_start', beforeWeekStart)
    .order('week_start', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as ThreeStepWeeklyEventRow | null) ?? null
}

/**
 * Read-only: my entry row for an event, or null. The hub uses this so
 * looking at the weekly never creates a row; the play screen owns row
 * creation.
 */
export async function fetchMyThreeStepWeeklyEntry(
  eventId: string,
  profileId: string,
): Promise<ThreeStepWeeklyEntryRow | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('three_step_weekly_entries')
    .select('*')
    .eq('event_id', eventId)
    .eq('profile_id', profileId)
    .maybeSingle()
  if (error) throw error
  return (data as ThreeStepWeeklyEntryRow | null) ?? null
}

/** Ensures the caller has an entry row for this event. */
export async function ensureMyThreeStepWeeklyEntry(
  eventId: string,
): Promise<ThreeStepWeeklyEntryRow> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('ensure_my_three_step_weekly_entry', {
    p_event_id: eventId,
  })
  if (error) throw error
  return data as ThreeStepWeeklyEntryRow
}

/** Delete my (unlocked) entry entirely — the editor's X-out. */
export async function withdrawThreeStepWeeklyEntry(entryId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc('withdraw_three_step_weekly_entry', {
    p_entry_id: entryId,
  })
  if (error) throw error
}

/** Save the three-step solution. Server enforces 160-char cap + blocklist. */
export async function saveThreeStepWeeklyEntry(
  entryId: string,
  step1: string,
  step2: string,
  step3: string,
): Promise<ThreeStepWeeklyEntryRow> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('save_three_step_weekly_entry', {
    p_entry_id: entryId,
    p_step1: step1,
    p_step2: step2,
    p_step3: step3,
  })
  if (error) throw error
  return data as ThreeStepWeeklyEntryRow
}

/**
 * Toggle a vote on an entry. Returns the entry's new total. The RPC
 * also enforces the 2-vote-per-event cap and blocks self-votes.
 */
export async function castThreeStepWeeklyVote(entryId: string): Promise<number> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('cast_three_step_weekly_vote', {
    p_entry_id: entryId,
  })
  if (error) throw error
  return Number(data ?? 0)
}

/** Report an entry. Idempotent per (entry, reporter). */
export async function reportThreeStepWeeklyEntry(
  entryId: string,
  reason: string | null = null,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc('report_three_step_weekly_entry', {
    p_entry_id: entryId,
    p_reason: reason,
  })
  if (error) throw error
}

/**
 * All entries for a given event. During the active write week, RLS
 * only returns the caller's own row — once the week is finalized it
 * returns everyone's. The screens shape themselves around that.
 */
export async function fetchThreeStepWeeklyEntries(
  eventId: string,
): Promise<Array<ThreeStepWeeklyEntryRow & { author_name: string | null; author_avatar: string | null }>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('three_step_weekly_entries')
    .select('*, profiles!inner(display_name, avatar_url)')
    .eq('event_id', eventId)
    .order('total_votes', { ascending: false })
    .order('updated_at', { ascending: true })
  if (error) throw error
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data ?? []).map((row: any) => ({
    ...row,
    author_name: row.profiles?.display_name ?? null,
    author_avatar: row.profiles?.avatar_url ?? null,
  }))
}

/** Which entries the caller has voted for in a given event. */
export async function fetchMyThreeStepWeeklyVotes(
  eventId: string,
): Promise<Set<string>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from('three_step_weekly_votes')
    .select('entry_id, three_step_weekly_entries!inner(event_id)')
    .eq('three_step_weekly_entries.event_id', eventId)
  if (error) throw error
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return new Set((data ?? []).map((r: any) => r.entry_id as string))
}

/** Top scorer per finalized week. `exclude` filters out a specific week_start. */
export async function fetchThreeStepWeeklyWinners(
  excludeWeekStart?: string,
): Promise<ThreeStepWeeklyWinnerRow[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('list_three_step_weekly_winners', {
    p_exclude: excludeWeekStart ?? null,
    // Effectively uncapped, so the archive can never fall behind the
    // trophy shelf. See the note in fetchWeeklyWinners.
    p_limit: 260,
  })
  if (error) throw error
  return Array.isArray(data) ? (data as ThreeStepWeeklyWinnerRow[]) : []
}

// ─── Date helpers (mirror Colorful Weekly so the two stay in lock-step) ─

export function thisMondayISO(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const dow = d.getUTCDay() // 0 = Sunday
  const daysSinceMonday = (dow + 6) % 7
  d.setUTCDate(d.getUTCDate() - daysSinceMonday)
  return d.toISOString().slice(0, 10)
}

/**
 * Use IET's canonical countdown so all three games show the same
 * number on the same screen. The previous local implementation
 * counted to UTC midnight Monday — IET counts to ET midnight Sunday,
 * which is later in UTC and therefore one more "day" remaining for
 * users in the Americas. Sharing the function fixes the off-by-one.
 */
export function daysUntilNextMonday(weekStart: string): number {
  return canonicalDaysLeftInWeek(weekStart)
}

/** "Week of Jun 29 – Jul 5" — which week a submission lands in. UTC on
 *  purpose: weeks flip at midnight UTC Sunday→Monday, and showing the range
 *  keeps a Monday-morning session from being mistaken for last week. */
export function weekRangeLabel(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00Z')
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6)
  const fmt = (d: Date) => d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric',
  })
  return `Week of ${fmt(start)} – ${fmt(end)}`
}

export function weekLabel(weekStart: string): string {
  const d = new Date(weekStart + 'T00:00:00Z')
  return d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  })
}

/** Archive label — week-end (Sunday) date in caps, e.g. "MAY 04 2026". */
export function threeStepArchiveDateLabel(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00Z')
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6)
  return end.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
  }).replace(',', '').toUpperCase()
}
